import React from "react";
import { motion } from "framer-motion";
import {
  CalendarDays,
  Clock3,
  UserRound,
  Video,
  MapPin,
  Bell,
  Check,
  ArrowRight,
  ShieldCheck,
  Plus,
  CalendarCheck,
  RefreshCw,
  ClipboardList,
  Stethoscope,
} from "lucide-react";
import Navbar from "../../../DashBoardComponents/Navbar";

import { useDispatch } from "react-redux";
import {
  handleLoginSignUpModal,
  LOGINMODAL,
} from "../../../Redux/actions/commonActions";

const Scheduling = () => {

  const dispatch = useDispatch();

  const handleLoginModal = () => {
    dispatch(
      handleLoginSignUpModal({
        data: true,
        actionType: LOGINMODAL,
      })
    );
  };
  const schedulingFeatures = [
    {
      icon: CalendarDays,
      title: "Calendar Management",
      description:
        "Manage your availability, appointments and healthcare schedule through one organised calendar.",
    },
    {
      icon: CalendarCheck,
      title: "Appointment Scheduling",
      description:
        "Create and manage patient appointments while keeping your schedule up to date.",
    },
    {
      icon: Bell,
      title: "Appointment Reminders",
      description:
        "Support appointment reminders and notifications through connected scheduling workflows.",
    },
    {
      icon: RefreshCw,
      title: "Rescheduling",
      description:
        "Manage changes to appointments and keep your calendar synchronised with supported workflows.",
    },
  ];

  const steps = [
    {
      number: "01",
      icon: CalendarDays,
      title: "Set Availability",
      description:
        "Define available time slots and organise your professional schedule.",
    },
    {
      number: "02",
      icon: UserRound,
      title: "Schedule Patient",
      description:
        "Select a patient and book an appropriate appointment slot.",
    },
    {
      number: "03",
      icon: ClipboardList,
      title: "Manage Appointment",
      description:
        "Review appointment details and make changes when required.",
    },
    {
      number: "04",
      icon: Bell,
      title: "Stay on Track",
      description:
        "Keep patients and healthcare professionals informed through supported reminders.",
    },
  ];

  const appointmentTypes = [
    {
      icon: Stethoscope,
      title: "In-Person Visits",
      description:
        "Schedule appointments for patients visiting a healthcare facility.",
    },
    {
      icon: Video,
      title: "Virtual Appointments",
      description:
        "Schedule supported virtual consultations and digital healthcare visits.",
    },
    {
      icon: Clock3,
      title: "Follow-Up Visits",
      description:
        "Plan follow-up appointments and continue the patient's care journey.",
    },
    {
      icon: CalendarCheck,
      title: "Routine Appointments",
      description:
        "Organise recurring or routine healthcare appointments where supported.",
    },
  ];

  const benefits = [
    "Manage your professional calendar",
    "Schedule patient appointments",
    "View upcoming appointments",
    "Support virtual and in-person visits",
    "Manage appointment changes",
    "Keep patients informed through supported reminders",
  ];

  const faqs = [
    {
      question: "What is healthcare scheduling?",
      answer:
        "Healthcare scheduling is the process of organising patient appointments, provider availability and related calendar information.",
    },
    {
      question: "Can healthcare professionals manage their availability?",
      answer:
        "Where scheduling functionality is supported, professionals can define and manage available appointment slots.",
    },
    {
      question: "Can I schedule virtual consultations?",
      answer:
        "Where virtual care is enabled, supported virtual consultations can be scheduled through the connected appointment workflow.",
    },
    {
      question: "Can appointments be rescheduled?",
      answer:
        "Supported scheduling workflows can allow appointment details and times to be changed when required.",
    },
    {
      question: "Are patients notified about appointments?",
      answer:
        "Where notification functionality is enabled, supported appointment reminders and updates can be communicated to patients.",
    },
  ];

  return (
    <>
      <Navbar />

      <main className="bg-white text-slate-900 overflow-hidden">

        {/* =====================================================
            HERO
        ====================================================== */}
        <section className="relative overflow-hidden bg-[#f4fbfc]">

          <div className="absolute inset-0 pointer-events-none">

            <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-teal-200/25 blur-3xl" />

            <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-cyan-200/25 blur-3xl" />

            <div
              className="absolute inset-0 opacity-[0.025]"
              style={{
                backgroundImage:
                  "linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)",
                backgroundSize: "48px 48px",
              }}
            />

          </div>

          <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-16 items-center">

            {/* LEFT */}
            <motion.div
              initial={{ opacity: 0, x: -35 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >

              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-teal-100 px-4 py-2 shadow-sm">

                <span className="w-2 h-2 rounded-full bg-teal-500" />

                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700">
                  Healthcare Scheduling
                </span>

              </div>

              <h1 className="font-sora text-5xl md:text-6xl lg:text-[62px] font-medium tracking-[-0.055em] leading-[1.03] mt-7">

                Make every
                <span className="block text-teal-500 mt-2">
                  appointment count.
                </span>

              </h1>

              <p className="text-slate-600 text-lg md:text-xl leading-8 mt-7 max-w-xl">
                Manage provider availability, patient appointments and
                scheduling workflows through one connected healthcare
                experience.
              </p>

              {/* HERO ACTION */}
              <div className="mt-9 rounded-2xl bg-white border border-slate-100 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.10)]">

                <div className="flex items-center gap-4">

                  <div className="w-12 h-12 rounded-xl bg-teal-50 grid place-items-center">

                    <CalendarDays
                      size={22}
                      className="text-teal-600"
                    />

                  </div>

                  <div className="flex-1">

                    <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
                      Scheduling Workspace
                    </p>

                    <p className="font-semibold mt-1">
                      Manage your healthcare calendar
                    </p>

                  </div>

                  <motion.button
                    onClick={handleLoginModal}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className="hidden sm:inline-flex items-center gap-2 rounded-xl bg-slate-950 text-white px-5 h-12 text-sm font-semibold"
                  >
                    Open Calendar
                    <ArrowRight size={15} />
                  </motion.button>
                </div>

                <button
                  onClick={() => handleLoginModal(true)}
                  className="sm:hidden w-full mt-4 h-12 rounded-xl bg-slate-950 text-white font-semibold text-sm"
                >
                  Open Calendar
                </button>
              </div>

              <div className="flex flex-wrap gap-6 mt-7 text-sm text-slate-500">

                <span className="inline-flex items-center gap-2">
                  <ShieldCheck size={16} className="text-teal-500" />
                  Organised scheduling
                </span>

                <span className="inline-flex items-center gap-2">
                  <CalendarCheck size={16} className="text-teal-500" />
                  Appointment management
                </span>

              </div>

            </motion.div>

            {/* RIGHT CALENDAR */}
            <motion.div
              initial={{ opacity: 0, x: 35, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="relative"
            >

              <div className="absolute -inset-8 rounded-[45px] bg-teal-200/30 blur-3xl" />

              <div className="relative bg-white rounded-[30px] border border-slate-100 shadow-[0_30px_80px_rgba(15,23,42,0.14)] p-6 md:p-7">

                <div className="flex items-center justify-between pb-5 border-b border-slate-100">

                  <div>

                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
                      Provider Calendar
                    </p>

                    <h3 className="font-sora text-xl font-semibold mt-1">
                      Today's Schedule
                    </h3>

                  </div>

                  <div className="w-11 h-11 rounded-xl bg-teal-50 grid place-items-center">

                    <CalendarDays
                      size={21}
                      className="text-teal-600"
                    />

                  </div>

                </div>

                {/* DATE */}
                <div className="mt-6 flex items-center justify-between">

                  <div>

                    <p className="text-[10px] uppercase tracking-wider text-slate-400">
                      Today
                    </p>

                    <p className="font-sora text-lg font-semibold mt-1">
                      Your Appointments
                    </p>

                  </div>

                  <button className="w-10 h-10 rounded-xl bg-slate-950 text-white grid place-items-center">
                    <Plus size={18} />
                  </button>

                </div>

                {/* APPOINTMENTS */}
                <div className="space-y-3 mt-5">

                  {[
                    ["09:00 AM", "Patient Consultation", Video],
                    ["11:30 AM", "Follow-Up Visit", Stethoscope],
                    ["02:00 PM", "Routine Appointment", MapPin],
                  ].map(([time, title, Icon], index) => (

                    <motion.div
                      key={`${time}-${title}`}
                      whileHover={{ x: 4 }}
                      className={`flex items-center gap-3 rounded-2xl p-4 border ${index === 0
                          ? "border-teal-100 bg-teal-50/60"
                          : "border-slate-100 bg-slate-50"
                        }`}
                    >

                      <div className="text-center w-16 shrink-0">

                        <p className="text-[10px] text-slate-400">
                          TIME
                        </p>

                        <p className="text-xs font-bold mt-1">
                          {time}
                        </p>

                      </div>

                      <div className="w-9 h-9 rounded-lg bg-white grid place-items-center">

                        <Icon
                          size={16}
                          className="text-teal-600"
                        />

                      </div>

                      <div className="flex-1">

                        <p className="text-xs font-semibold">
                          {title}
                        </p>

                        <p className="text-[10px] text-slate-400 mt-1">
                          Patient appointment
                        </p>

                      </div>

                      <Check
                        size={15}
                        className="text-teal-500"
                      />

                    </motion.div>

                  ))}

                </div>

                {/* AVAILABILITY */}
                <div className="mt-5 rounded-2xl bg-slate-950 text-white p-5">

                  <div className="flex items-center gap-3">

                    <div className="w-9 h-9 rounded-xl bg-teal-500/10 grid place-items-center">

                      <Clock3
                        size={17}
                        className="text-teal-400"
                      />

                    </div>

                    <div>

                      <p className="text-xs font-semibold">
                        Availability
                      </p>

                      <p className="text-[10px] text-slate-400 mt-1">
                        Manage your available time slots
                      </p>

                    </div>

                  </div>

                </div>

              </div>

            </motion.div>

          </div>

        </section>

        {/* =====================================================
            FEATURES
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-7xl mx-auto px-6">

            <div className="max-w-2xl">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Scheduling Features
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Your schedule,
                <span className="text-teal-500">
                  {" "}under control.
                </span>

              </h2>

              <p className="text-slate-600 leading-7 mt-5">
                Organise appointments and availability while keeping your
                healthcare workflow moving smoothly.
              </p>

            </div>

            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">

              {schedulingFeatures.map((item, index) => {

                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.55,
                      delay: index * 0.07,
                    }}
                    whileHover={{ y: -7 }}
                    className="group rounded-3xl border border-slate-100 bg-slate-50/70 p-7 hover:bg-white hover:shadow-xl hover:border-teal-100 transition"
                  >

                    <div className="w-12 h-12 rounded-2xl bg-teal-50 grid place-items-center group-hover:bg-teal-500 transition">

                      <Icon
                        size={22}
                        className="text-teal-600 group-hover:text-white transition"
                      />

                    </div>

                    <h3 className="font-sora text-lg font-semibold mt-6">
                      {item.title}
                    </h3>

                    <p className="text-slate-500 text-sm leading-7 mt-3">
                      {item.description}
                    </p>

                    <div className="inline-flex items-center gap-2 text-teal-600 text-sm font-semibold mt-6">
                      Explore
                      <ArrowRight size={15} />
                    </div>

                  </motion.div>
                );

              })}

            </div>

          </div>

        </section>

        {/* =====================================================
            HOW IT WORKS
        ====================================================== */}
        <section className="py-24 md:py-28 bg-[#f6fbfc]">

          <div className="max-w-6xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                How It Works
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Scheduling made
                <span className="text-teal-500">
                  {" "}simpler.
                </span>

              </h2>

            </div>

            <div className="relative grid md:grid-cols-4 gap-6 mt-14">

              <div className="hidden md:block absolute top-7 left-[11%] right-[11%] h-px bg-teal-100" />

              {steps.map((item) => {

                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.number}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="relative text-center"
                  >

                    <div className="relative z-10 mx-auto w-14 h-14 rounded-2xl bg-slate-950 border-4 border-[#f6fbfc] shadow-lg grid place-items-center">

                      <Icon
                        size={21}
                        className="text-teal-400"
                      />

                    </div>

                    <span className="block mt-5 text-[10px] font-bold tracking-[0.2em] text-teal-600">
                      STEP {item.number}
                    </span>

                    <h3 className="font-sora text-lg font-semibold mt-2">
                      {item.title}
                    </h3>

                    <p className="text-slate-500 text-sm leading-6 mt-2 max-w-[230px] mx-auto">
                      {item.description}
                    </p>

                  </motion.div>
                );

              })}

            </div>

          </div>

        </section>

        {/* =====================================================
            APPOINTMENT TYPES
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-6xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Appointment Types
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Schedule care for
                <span className="text-teal-500">
                  {" "}every need.
                </span>

              </h2>

              <p className="text-slate-600 leading-7 mt-5">
                Support different appointment types within one connected
                scheduling workflow.
              </p>

            </div>
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-8">

              {appointmentTypes.map((item, index) => {

                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.06 }}
                    whileHover={{ y: -4 }}
                    className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm hover:shadow-md hover:border-teal-100 transition"
                  >

                    <div className="w-9 h-9 rounded-lg bg-teal-50 grid place-items-center">
                      <Icon
                        size={17}
                        className="text-teal-600"
                      />
                    </div>

                    <h3 className="font-sora text-sm font-semibold mt-3">
                      {item.title}
                    </h3>

                    <p className="text-slate-500 text-[11px] leading-5 mt-1.5">
                      {item.description}
                    </p>

                  </motion.div>
                );
              })}

            </div>

          </div>

        </section>

        {/* =====================================================
            CONNECTED CARE
        ====================================================== */}
        <section className="py-24 md:py-28 bg-slate-950 text-white">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">

            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-400">
                Connected Scheduling
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Scheduling that fits
                <span className="block text-teal-400">
                  into your care workflow.
                </span>

              </h2>

              <p className="text-slate-400 text-lg leading-8 mt-6 max-w-xl">
                Connect appointments with virtual care, patient records and
                other supported healthcare services.
              </p>

            </div>

            <div className="space-y-3">

              {[
                [CalendarCheck, "Patient Appointments"],
                [Video, "Virtual Care"],
                [ClipboardList, "Patient Records"],
                [Bell, "Notifications"],
              ].map(([Icon, title], index) => (

                <motion.div
                  key={title}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.07 }}
                  whileHover={{ x: 5 }}
                  className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4"
                >

                  <div className="w-10 h-10 rounded-xl bg-teal-500/10 grid place-items-center">

                    <Icon
                      size={18}
                      className="text-teal-400"
                    />

                  </div>

                  <span className="text-sm font-medium text-slate-300 flex-1">
                    {title}
                  </span>

                  <ArrowRight
                    size={16}
                    className="text-slate-500"
                  />

                </motion.div>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            BENEFITS
        ====================================================== */}
        <section className="py-24 md:py-28 bg-gradient-to-br from-cyan-50 to-blue-50">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">

            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Provider Benefits
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Spend less time
                <span className="block text-teal-500">
                  managing your calendar.
                </span>

              </h2>

              <p className="text-slate-600 leading-8 mt-6 max-w-xl">
                Keep appointments, availability and scheduling information
                organised so you can focus more on patient care.
              </p>

            </div>

            <div className="space-y-3">

              {benefits.map((item, index) => (

                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ x: 5 }}
                  className="flex items-center gap-4 rounded-2xl border border-white bg-white/80 p-4 shadow-sm"
                >

                  <span className="w-8 h-8 rounded-full bg-teal-50 text-teal-600 grid place-items-center shrink-0">

                    <Check size={15} />

                  </span>

                  <span className="text-sm md:text-base font-medium text-slate-700">
                    {item}
                  </span>

                </motion.div>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            FAQ
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-4xl mx-auto px-6">

            <div className="text-center">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                FAQ
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">
                Frequently asked questions
              </h2>

            </div>

            <div className="space-y-3 mt-12">

              {faqs.map((faq, index) => (

                <motion.details
                  key={faq.question}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="group rounded-2xl border border-slate-100 bg-slate-50 open:bg-white open:shadow-sm"
                >

                  <summary className="cursor-pointer list-none flex items-center justify-between gap-5 p-6 font-sora font-semibold">

                    <span>
                      {faq.question}
                    </span>

                    <span className="w-8 h-8 rounded-full bg-white group-open:bg-teal-500 group-open:text-white grid place-items-center shrink-0 transition">

                      <ArrowRight
                        size={15}
                        className="group-open:rotate-90 transition-transform"
                      />

                    </span>

                  </summary>

                  <p className="px-6 pb-6 text-slate-600 leading-7 text-sm md:text-base">
                    {faq.answer}
                  </p>

                </motion.details>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            FINAL CTA
        ====================================================== */}
        <section className="px-6 pb-24">

          <div className="relative overflow-hidden max-w-7xl mx-auto rounded-[32px] bg-slate-950 text-white px-8 py-16 md:px-16 text-center">

            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-teal-500/15 blur-3xl" />

            <div className="absolute -bottom-28 -left-20 w-72 h-72 rounded-full bg-cyan-500/10 blur-3xl" />

            <div className="relative">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-400">
                Healthcare Professionals
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Make scheduling part of
                <span className="text-teal-400">
                  {" "}connected care.
                </span>

              </h2>

              <p className="text-slate-300 max-w-2xl mx-auto mt-5 leading-7">
                Manage appointments, availability and supported healthcare
                scheduling workflows through Doxzon.
              </p>

              <button
                onClick={() => handleLoginModal(true)}
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-7 py-4 font-semibold hover:bg-teal-50 transition"
              >
                Explore Scheduling
                <ArrowRight size={17} />
              </button>

            </div>

          </div>

        </section>

      </main>
    </>
  );
};

export default Scheduling;